import { Button, Col, Input, Row, Table, Typography, message, Card, Modal, Space, Popconfirm } from "antd"
import { EditOutlined, DeleteOutlined, CheckOutlined } from "@ant-design/icons"
import React, { useEffect, useState } from "react"
import PageHeader from "../../../components/Elements/PageHeader"
import { UserOutlined } from "@ant-design/icons"
import { collection, onSnapshot, orderBy, query, addDoc, serverTimestamp, deleteDoc, doc, updateDoc } from "firebase/firestore"
import { db } from "../../../firebase/config"
import { useAuthContext } from "../../../hooks/useAuthContext"

const { Text } = Typography

export default function DeniedClients() {
  const { user } = useAuthContext()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)
  const [formLoading, setFormLoading] = useState(false)
  const [clientName, setClientName] = useState("")
  const [vat, setVat] = useState("")
  const [reg, setReg] = useState("")
  const [editingId, setEditingId] = useState(null)
  const [editingClient, setEditingClient] = useState(null)
  const [editClientName, setEditClientName] = useState("")
  const [editVat, setEditVat] = useState("")
  const [editReg, setEditReg] = useState("")
  const [editLoading, setEditLoading] = useState(false)
  const [bypassEmails, setBypassEmails] = useState([])
  const [newBypassEmail, setNewBypassEmail] = useState("")
  const [bypassModalId, setBypassModalId] = useState(null)
  const [bypassModalClient, setBypassModalClient] = useState(null)
  const [bypassModalEmails, setBypassModalEmails] = useState([])
  const [newBypassModalEmail, setNewBypassModalEmail] = useState("")
  const [bypassModalLoading, setBypassModalLoading] = useState(false)

  useEffect(() => {
    setLoading(true)
    const deniedClientsRef = collection(db, "denied_clients")
    const deniedClientsQuery = query(deniedClientsRef, orderBy("client", "asc"))

    const unsubscribe = onSnapshot(deniedClientsQuery, (snapshot) => {
      const results = snapshot.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
      }))
      setData(results)
      setLoading(false)
    })

    return () => unsubscribe()
  }, [])

  const handleAddDeniedClient = async () => {
    if (!clientName.trim() || !vat.trim() || !reg.trim()) {
      message.error("All fields are required")
      return
    }

    setFormLoading(true)
    try {
      const deniedClientsRef = collection(db, "denied_clients")
      await addDoc(deniedClientsRef, {
        client: clientName.trim(),
        vat: vat.trim(),
        reg: reg.trim(),
        created: serverTimestamp(),
        createdBy: user.uid,
      })

      message.success("Denied client added successfully!")
      setClientName("")
      setVat("")
      setReg("")
    } catch (error) {
      console.error("Error adding denied client:", error)
      message.error("Failed to add denied client")
    } finally {
      setFormLoading(false)
    }
  }

  const handleEditClick = (record) => {
    setEditingId(record.id)
    setEditingClient(record)
    setEditClientName(record.client)
    setEditVat(record.vat)
    setEditReg(record.reg)
    setBypassEmails(record.bypassEmails || [])
    setNewBypassEmail("")
  }

  const handleEditSubmit = async () => {
    if (!editClientName.trim() || !editVat.trim() || !editReg.trim()) {
      message.error("All fields are required")
      return
    }

    setEditLoading(true)
    try {
      const clientRef = doc(db, "denied_clients", editingId)
      await updateDoc(clientRef, {
        client: editClientName.trim(),
        vat: editVat.trim(),
        reg: editReg.trim(),
        bypassEmails: bypassEmails,
      })

      message.success("Denied client updated successfully!")
      setEditingId(null)
      setEditingClient(null)
    } catch (error) {
      console.error("Error updating denied client:", error)
      message.error("Failed to update denied client")
    } finally {
      setEditLoading(false)
    }
  }

  const handleAddBypassEmail = () => {
    const trimmedEmail = newBypassEmail.trim().toLowerCase()

    if (!trimmedEmail) {
      message.error("Please enter an email")
      return
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      message.error("Please enter a valid email")
      return
    }

    if (bypassEmails.includes(trimmedEmail)) {
      message.warning("This email is already in the bypass list")
      return
    }

    setBypassEmails([...bypassEmails, trimmedEmail])
    setNewBypassEmail("")
  }

  const handleRemoveBypassEmail = (email) => {
    setBypassEmails(bypassEmails.filter((e) => e !== email))
  }

  const handleDeleteClient = async (id) => {
    try {
      const clientRef = doc(db, "denied_clients", id)
      await deleteDoc(clientRef)
      message.success("Denied client deleted!")
    } catch (error) {
      console.error("Error deleting denied client:", error)
      message.error("Failed to delete denied client")
    }
  }

  const handleBypassClick = (record) => {
    setBypassModalId(record.id)
    setBypassModalClient(record)
    setBypassModalEmails(record.bypassEmails || [])
    setNewBypassModalEmail("")
  }

  const handleAddBypassModalEmail = () => {
    const trimmedEmail = newBypassModalEmail.trim().toLowerCase()

    if (!trimmedEmail) {
      message.error("Please enter an email")
      return
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      message.error("Please enter a valid email")
      return
    }

    if (bypassModalEmails.includes(trimmedEmail)) {
      message.warning("This email is already in the bypass list")
      return
    }

    setBypassModalEmails([...bypassModalEmails, trimmedEmail])
    setNewBypassModalEmail("")
  }

  const handleRemoveBypassModalEmail = (email) => {
    setBypassModalEmails(bypassModalEmails.filter((e) => e !== email))
  }

  const handleSaveBypassEmails = async () => {
    setBypassModalLoading(true)
    try {
      const clientRef = doc(db, "denied_clients", bypassModalId)
      await updateDoc(clientRef, {
        bypassEmails: bypassModalEmails,
      })
      message.success("Bypass emails updated!")
      setBypassModalId(null)
      setBypassModalClient(null)
    } catch (error) {
      console.error("Error updating bypass emails:", error)
      message.error("Failed to update bypass emails")
    } finally {
      setBypassModalLoading(false)
    }
  }

  const columns = [
    {
      title: "Client",
      dataIndex: "client",
      key: "client",
      align: "left",
      width: "35%",
      sorter: (a, b) => (a.client || "").localeCompare(b.client || ""),
    },
    {
      title: "VAT",
      dataIndex: "vat",
      key: "vat",
      align: "left",
      width: "20%",
    },
    {
      title: "Registration",
      dataIndex: "reg",
      key: "reg",
      align: "left",
      width: "20%",
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      width: "25%",
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditClick(record)}>
            Edit
          </Button>
          <Popconfirm
            title="Delete Client"
            description="Are you sure you want to delete this denied client?"
            onConfirm={() => handleDeleteClient(record.id)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: true }}>
            <Button type="primary" danger size="small" icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
          <Button
            type="default"
            size="small"
            icon={<CheckOutlined />}
            onClick={() => handleBypassClick(record)}>
            Bypass
          </Button>
        </Space>
      ),
    },
  ]

  return (
    <div style={{ padding: 15 }}>
      <Row gutter={[15, 15]}>
        <Col span={24}>
          <PageHeader icon={<UserOutlined />} text="Denied Clients" />
        </Col>
        <Col span={16}>
          <Table
            size="small"
            columns={columns}
            dataSource={data}
            loading={loading}
            rowKey={(record) => record.id}
            pagination={{ defaultPageSize: 50, showSizeChanger: true }}
            bordered
          />
        </Col>
        <Col span={8}>
          <Card title="Add Denied Client" bordered>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <Text strong style={{ display: "block", marginBottom: 4 }}>
                  Client Name
                </Text>
                <Input
                  placeholder="Enter client name"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  disabled={formLoading}
                />
              </div>

              <div>
                <Text strong style={{ display: "block", marginBottom: 4 }}>
                  VAT
                </Text>
                <Input
                  placeholder="Enter VAT"
                  value={vat}
                  onChange={(e) => setVat(e.target.value)}
                  disabled={formLoading}
                />
              </div>

              <div>
                <Text strong style={{ display: "block", marginBottom: 4 }}>
                  Registration
                </Text>
                <Input
                  placeholder="Enter registration"
                  value={reg}
                  onChange={(e) => setReg(e.target.value)}
                  disabled={formLoading}
                />
              </div>

              <Button
                type="primary"
                onClick={handleAddDeniedClient}
                loading={formLoading}
                block>
                Add
              </Button>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Edit Modal */}
      <Modal
        title="Edit Denied Client"
        open={editingId !== null}
        onOk={handleEditSubmit}
        onCancel={() => setEditingId(null)}
        confirmLoading={editLoading}
        okText="Save"
        width={500}>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <Text strong style={{ display: "block", marginBottom: 4 }}>
              Client Name
            </Text>
            <Input
              placeholder="Enter client name"
              value={editClientName}
              onChange={(e) => setEditClientName(e.target.value)}
              disabled={editLoading}
            />
          </div>

          <div>
            <Text strong style={{ display: "block", marginBottom: 4 }}>
              VAT
            </Text>
            <Input
              placeholder="Enter VAT"
              value={editVat}
              onChange={(e) => setEditVat(e.target.value)}
              disabled={editLoading}
            />
          </div>

          <div>
            <Text strong style={{ display: "block", marginBottom: 4 }}>
              Registration
            </Text>
            <Input
              placeholder="Enter registration"
              value={editReg}
              onChange={(e) => setEditReg(e.target.value)}
              disabled={editLoading}
            />
          </div>

          <div style={{ borderTop: "1px solid #f0f0f0", paddingTop: 12 }}>
            <Text strong style={{ display: "block", marginBottom: 8 }}>
              Bypass Emails
            </Text>
            <Space direction="vertical" style={{ width: "100%" }}>
              <div style={{ display: "flex", gap: 8 }}>
                <Input
                  placeholder="Enter email to bypass"
                  value={newBypassEmail}
                  onChange={(e) => setNewBypassEmail(e.target.value)}
                  disabled={editLoading}
                  onPressEnter={handleAddBypassEmail}
                  style={{ flex: 1 }}
                />
                <Button
                  type="primary"
                  onClick={handleAddBypassEmail}
                  disabled={editLoading}
                  style={{ minWidth: 80 }}>
                  Add
                </Button>
              </div>

              {bypassEmails.length > 0 && (
                <div
                  style={{
                    border: "1px solid #f0f0f0",
                    borderRadius: 4,
                    padding: 8,
                    maxHeight: 200,
                    overflowY: "auto",
                  }}>
                  {bypassEmails.map((email) => (
                    <div
                      key={email}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "6px 8px",
                        borderBottom: "1px solid #f0f0f0",
                      }}>
                      <Text>{email}</Text>
                      <Button
                        type="text"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleRemoveBypassEmail(email)}
                        disabled={editLoading}
                      />
                    </div>
                  ))}
                </div>
              )}

              {bypassEmails.length === 0 && (
                <Text type="secondary" italic>
                  No bypass emails added yet
                </Text>
              )}
            </Space>
          </div>
        </div>
      </Modal>

      {/* Bypass Modal */}
      <Modal
        title={`Manage Bypass Emails - ${bypassModalClient?.client}`}
        open={bypassModalId !== null}
        onOk={handleSaveBypassEmails}
        onCancel={() => setBypassModalId(null)}
        confirmLoading={bypassModalLoading}
        okText="Save"
        width={450}>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Text strong>Add Email to Bypass List</Text>
          <div style={{ display: "flex", gap: 8 }}>
            <Input
              placeholder="Enter email to bypass"
              value={newBypassModalEmail}
              onChange={(e) => setNewBypassModalEmail(e.target.value)}
              disabled={bypassModalLoading}
              onPressEnter={handleAddBypassModalEmail}
              style={{ flex: 1 }}
            />
            <Button
              type="primary"
              onClick={handleAddBypassModalEmail}
              disabled={bypassModalLoading}>
              Add
            </Button>
          </div>

          <div style={{ marginTop: 12 }}>
            <Text strong style={{ display: "block", marginBottom: 8 }}>
              Bypass Emails List
            </Text>
            {bypassModalEmails.length > 0 ? (
              <div
                style={{
                  border: "1px solid #f0f0f0",
                  borderRadius: 4,
                  padding: 8,
                  maxHeight: 250,
                  overflowY: "auto",
                }}>
                {bypassModalEmails.map((email) => (
                  <div
                    key={email}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "8px",
                      borderBottom: "1px solid #f0f0f0",
                    }}>
                    <Text>{email}</Text>
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleRemoveBypassModalEmail(email)}
                      disabled={bypassModalLoading}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <Text type="secondary" italic>
                No bypass emails added yet
              </Text>
            )}
          </div>
        </div>
      </Modal>
    </div>
  )
}
